// React
import React, { FC, useContext, useState, useEffect, useRef } from 'react';

// Redux
import { useSelector, useDispatch } from 'react-redux';

// Context
import UserDetailsContext from 'Context/userDetailsContext';

// GraphQL
// broke the app
// eslint-disable-next-line import/no-extraneous-dependencies
import { useQuery } from '@apollo/react-hooks';

// Styles
import injectSheet from 'react-jss';

// Components
import { Loading } from 'Components/Loading';
import { Center } from 'Components/Helpers';

// Containers
import { GoodDataDashboardContainer } from 'Containers/GoodData/containers';

// Actions
import {
  setPlatformInfoEventValue,
  setDateRangeValue,
} from 'Containers/GoodData/actions/goodData.actions';

// Utilities
import { modifyGoodDataFilterContext } from 'Containers/GoodData/utilities';
import { getRelativeDateFromToday } from 'Utils/getRelativeDateFromToday';
import { areEqual } from 'Utils/areEqual';
import { formatDatesForGoodData } from 'Containers/GoodData/utilities/formatDatesForGoodData';
import { formatDatesForCalendar } from 'Containers/GoodData/utilities/formatDatesForCalendar';
import { ProductAnalyticsContext } from 'Context/ProductAnalytics';
import { useRouter } from 'Utils/router';
import withErrorBoundary from 'HOC/ErrorBoundary';
import Animations from '../../../../animations';
import { findDateFromDays } from '../../../../../utils/findDateFromDays';
import styleSheet from './styles';
import goodDataGql from './gqlQuery';

interface Props {
  dashboard: 'IWC' | 'VT';
  show?: boolean;
  filterValue: string;
  handleClose?: () => void;
  classes?: any;
}

const ContentPerformanceDetails: FC<Props> = (props: Props) => {
  const dispatch = useDispatch();
  const { platformInfoEvent, dateRange } = useSelector(
    (selectors: any) => selectors.goodData,
    areEqual
  );
  const { selection, fromDate, toDate } = dateRange;

  const {
    helpers: { View },
    actions: { track },
  } = useContext(ProductAnalyticsContext);
  const { location } = useRouter();

  // State that holds the GraphQL data.
  const [gdGQLData, setGDGQLData] = useState(undefined);

  // State that holds the dashboardConfiguration returned from GoodDataDashboardContainer
  const [dashboardConfiguration, setDashboardConfiguration] =
    useState(undefined);

  // Use this state to keep track of how many label.value.change
  // events have occured in order to know how long to display our
  // loading animation
  const [isLoading, setIsLoading] = useState(true);

  const { dashboard, show, filterValue, handleClose, classes } = props;

  // These two constants are the dashboard names as the back-end knows them.
  const VT_DETAILS_DASHBOARD_NAME = 'analytics_cp_vt_details_v3';
  const IWC_DETAILS_DASHBOARD_NAME = 'analytics_cp_iwc_details_v2';

  // These constants are the labels that are modified in modifyGoodDataFilterContext
  // calls for this dashboard.  Date is obviously the date filter and location is
  // applied upon hearing platform.info events to route the user where they initially
  // drilled into the ContentPerformanceOverview Dashboard.
  const DATE_FILTER_LABEL = 'report_date.date.day.us.mm_dd_yyyy';
  const LOCATION_FILTER_LABEL = 'label.location_info_cleaned.location_name';
  const INST_FILTER_LABEL =
    'label.cp_institution_daily_summary_with_network.institution_id';

  const userDetails = useContext(UserDetailsContext);

  // GraphQL User.GoodDataClaims Query
  const { data: goodDataData } = useQuery(goodDataGql, {
    variables: {
      id: userDetails.userId,
      accountId: userDetails.accountId,
      instId: userDetails.instId,
    },
    skip: gdGQLData,
  });

  // Use this as a place to dispatch for Combined Analytics.
  // Package drilledInDashboard and filterValue to know
  // which dashboard they drilled into and the location they
  // have filtered to.
  useEffect(() => {
    if (dashboard && filterValue) {
      // Combined Analytics event tracking - This shows that the user clicked on the link
      track('location_clicked', {
        screen: View(location.pathname),
        section: `${
          dashboard === 'VT' ? 'Virtual Tour' : 'Integrated Web Content(IWC)'
        }`,
        location_name: filterValue,
      });
    }
  }, [dashboard, filterValue]);

  // useEffect to update state when the goodDataData changes.  Should
  // really only happen once.
  useEffect(() => {
    if (goodDataData && !areEqual(goodDataData, gdGQLData)) {
      setGDGQLData(goodDataData);
    }
  }, [goodDataData]);

  // The first time the dashboard has loaded, change the filter context to 30 Days,
  // based on the dashboard prop (Product requirement).
  useEffect(() => {
    if (show && dashboard && platformInfoEvent && gdGQLData) {
      modifyGoodDataFilterContext(
        (dashboard === 'VT' && VT_DETAILS_DASHBOARD_NAME) ||
          IWC_DETAILS_DASHBOARD_NAME,
        [
          {
            label: DATE_FILTER_LABEL,
            from: fromDate,
            to: toDate,
            type: 'date',
          },
          {
            label: INST_FILTER_LABEL,
            value: gdGQLData.institution.instGuid,
          },
        ]
      );
      modifyGoodDataFilterContext(
        (dashboard === 'VT' && VT_DETAILS_DASHBOARD_NAME) ||
          IWC_DETAILS_DASHBOARD_NAME,
        [
          {
            label: LOCATION_FILTER_LABEL,
            value: filterValue,
          },
          {
            label: INST_FILTER_LABEL,
            value: gdGQLData.institution.instGuid,
          },
        ]
      );
      setPlatformInfoEventValue(dispatch, false);
    }
  }, [platformInfoEvent, gdGQLData]);

  // Hide loading screen when GoodData dashboard is loaded
  useEffect(() => {
    const listener = (event) => {
      if (typeof event.data !== 'string') return;
      const message = JSON.parse(event.data);

      if (
        message.gdc &&
        message.gdc.type === 'app.ok' &&
        message.gdc.name === 'embedded.gdc.loaded'
      ) {
        setIsLoading(false);
      }
    };

    window.addEventListener('message', listener);

    return () => {
      window.removeEventListener('message', listener);
    };
  }, []);

  // Loading timeout to ensure we will show GoodData even if we have an event issue
  useEffect(() => {
    let timeoutId;
    if (show && dashboard && dashboardConfiguration) {
      timeoutId = setTimeout(() => {
        if (isLoading) {
          setIsLoading(false);
        }
      }, 15000);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [show, dashboard, dashboardConfiguration, isLoading]);

  // Anytime a dropdown change happens the event filter context change
  // is propogated to both dashboards.
  const handleDropdownChange = (event: any) => {
    if (!areEqual(event.target, { label: 'Custom Date', value: 0 })) {
      setDateRangeValue(dispatch, {
        selection: event.target,
        fromDate: findDateFromDays(event.target.value),
        toDate: getRelativeDateFromToday(1),
      });
    } else {
      setDateRangeValue(dispatch, {
        selection: event.target,
        fromDate,
        toDate,
      });
    }
  };

  const handleCalendarChange = (
    selectedStartDate: string,
    selectedChangeEndDate: string
  ) => {
    setDateRangeValue(dispatch, {
      selection,
      fromDate: formatDatesForGoodData(selectedStartDate),
      toDate: formatDatesForGoodData(selectedChangeEndDate),
    });
  };

  useEffect(() => {
    if (show && dashboard && gdGQLData) {
      modifyGoodDataFilterContext(
        (dashboard === 'VT' && VT_DETAILS_DASHBOARD_NAME) ||
          IWC_DETAILS_DASHBOARD_NAME,
        [
          {
            label: DATE_FILTER_LABEL,
            from: fromDate,
            to: toDate,
            type: 'date',
          },
          {
            label: INST_FILTER_LABEL,
            value: gdGQLData.institution.instGuid,
          },
        ]
      );
    }
  }, [fromDate, toDate, gdGQLData]);

  let isAnimatingOut = false;
  const background = useRef();
  const card = useRef();

  const closeModal = () => {
    if (!isAnimatingOut) {
      Animations.modal.out(card, handleClose);
    }
    isAnimatingOut = true;
    setIsLoading(true);
  };
  const keyboardHandler = (e: KeyboardEvent) => {
    const key = e.key.toLowerCase();
    if (key === 'escape') {
      if (card.current) closeModal();
    }
  };

  useEffect(() => {
    if (show) {
      Animations.modal.in(card);
    } else {
      Animations.modal.out(card);
    }
    document.addEventListener('keydown', keyboardHandler, false);

    return () => {
      // unmount so remove keyboardListener from document
      document.removeEventListener('keydown', keyboardHandler, false);
    };
  }, [show]);

  return (
    <div className={classes.container}>
      <div className={classes.containerWrapper}>
        <div className={classes.center}>
          <button
            type="button"
            ref={background}
            onClick={!isLoading && closeModal}
            className={classes.closer}
            aria-label="Close modal"
          />
          <div ref={card} className={classes.card}>
            <div className={classes.cardWrapper}>
              {!isLoading && (
                <button
                  type="button"
                  className={classes.crossMark}
                  onClick={closeModal}
                  aria-label="Close modal"
                >
                  &#x2715;
                </button>
              )}
              {isLoading && (
                <div className={classes.loader}>
                  <Center>
                    <Loading />
                  </Center>
                </div>
              )}
              {show && dashboard && (
                <GoodDataDashboardContainer
                  title={
                    (dashboard === 'VT' &&
                      'Virtual Tour Content Performance Details Dashboard') ||
                    'Interactive Web Content Performance Details Dashboard'
                  }
                  name={
                    (dashboard === 'VT' && VT_DETAILS_DASHBOARD_NAME) ||
                    IWC_DETAILS_DASHBOARD_NAME
                  }
                  id={
                    (dashboard === 'VT' && VT_DETAILS_DASHBOARD_NAME) ||
                    IWC_DETAILS_DASHBOARD_NAME
                  }
                  gdData={gdGQLData}
                  width="940px"
                  height={(dashboard === 'VT' && '100%') || '100%'}
                  border="none"
                  handleDropdownChange={handleDropdownChange}
                  startDate={formatDatesForCalendar(fromDate)}
                  endDate={formatDatesForCalendar(toDate)}
                  selectedOption={selection}
                  handleCalendarChange={handleCalendarChange}
                  dashboardCallback={setDashboardConfiguration}
                  useGoodDataDropdown={
                    dashboardConfiguration && dashboardConfiguration.enabled
                  }
                  goodDataDropdownStyling={{
                    height: '100px',
                    width: '463px',
                    backgroundColor: '#FFFFFF',
                    position: 'relative',
                    marginBottom: '-62px',
                    zIndex: 8,
                    left: '620px',
                  }}
                  dashBoardName={dashboard}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

ContentPerformanceDetails.defaultProps = {
  show: false,
  handleClose: () => {},
  classes: {},
};

export default withErrorBoundary(
  injectSheet(styleSheet)(ContentPerformanceDetails)
);
